/**
 * Launcher button component for the Fyncall Widget
 */

import { createElement, addClass, removeClass, toggleClass, setText } from '../dom.js';

/**
 * Built-in launcher icons
 */
export const LAUNCHER_ICONS = {
    chat: '<svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>',
    chatDots: '<svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-9 9H7V9h4v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/></svg>',
    support: '<svg viewBox="0 0 24 24"><path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"/></svg>',
    wave: '<svg viewBox="0 0 24 24"><path d="M7.03 4.95L3.49 8.49c-3.32 3.32-3.32 8.7 0 12.02s8.7 3.32 12.02 0l6.01-6.01c.97-.97.97-2.56 0-3.54-.12-.12-.25-.23-.39-.32l.39-.39c.97-.97.97-2.56 0-3.54-.16-.16-.35-.3-.54-.41.4-.92.23-2.02-.52-2.77-.87-.87-2.22-.96-3.2-.28-.1-.15-.21-.29-.34-.42-.97-.97-2.56-.97-3.54 0l-.39.39c-.09-.14-.2-.27-.32-.39-.97-.97-2.56-.97-3.54 0zM9.17 6.36c.2-.2.51-.2.71 0l4.23 4.24-1.41 1.41-3.52-3.52-.01-.01V6.36zm2.83 2.83l.71-.71 4.24 4.24-.71.71-4.24-4.24z"/></svg>',
    smile: '<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-6c.78 2.34 2.72 4 5 4s4.22-1.66 5-4H7zm1-3c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1zm8 0c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/></svg>',
    send: '<svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>',
    message: '<svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>',
    help: '<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>'
};

/**
 * Get launcher icon HTML based on config
 * @param {Object} config - Widget configuration
 * @returns {string} Icon HTML
 */
export function getLauncherIcon(config) {
    const iconType = config.launcherIcon || 'chat';

    // Custom icon URL
    if (iconType === 'custom' && config.customLauncherIconUrl) {
        return `<img src="${config.customLauncherIconUrl}" alt="Chat" />`;
    }

    return LAUNCHER_ICONS[iconType] || LAUNCHER_ICONS.chat;
}

/**
 * Create launcher HTML
 * @param {Object} config - Widget configuration
 * @returns {string} Launcher HTML
 */
export function createLauncherHTML(config) {
    const style = config.launcherStyle || 'circle';
    const size = config.launcherSize || 'medium';
    const animation = config.launcherAnimation || 'none';
    const text = config.launcherText;

    const iconHTML = getLauncherIcon(config);
    const textHTML = text ? `<span class="launcher-text">${text}</span>` : '';

    const classes = [
        `launcher-${style}`,
        `launcher-${size}`,
        animation !== 'none' ? `animation-${animation}` : ''
    ].filter(Boolean).join(' ');

    return `
        <button id="fyncall-launcher" class="${classes}" aria-label="Open chat">
            ${iconHTML}
            ${textHTML}
            <span class="fyncall-badge" id="fyncall-badge"></span>
        </button>
    `;
}

/**
 * Create launcher element
 * @param {Object} config - Widget configuration
 * @param {Function} onClick - Click handler
 * @returns {HTMLElement} Launcher element
 */
export function createLauncher(config, onClick) {
    const launcher = createElement('button', {
        id: 'fyncall-launcher',
        className: `launcher-${config.launcherStyle || 'circle'} launcher-${config.launcherSize || 'medium'}${config.launcherAnimation !== 'none' ? ` animation-${config.launcherAnimation}` : ''}`,
        'aria-label': 'Open chat',
        onClick
    });

    launcher.innerHTML = `
        ${getLauncherIcon(config)}
        ${config.launcherText ? `<span class="launcher-text">${config.launcherText}</span>` : ''}
        <span class="fyncall-badge" id="fyncall-badge"></span>
    `;

    return launcher;
}

/**
 * Update badge count
 * @param {HTMLElement} badgeElement - Badge element
 * @param {number} count - Unread count
 */
export function updateBadge(badgeElement, count) {
    if (!badgeElement) return;

    setText(badgeElement, count.toString());
    toggleClass(badgeElement, 'show', count > 0);
}

/**
 * Create proactive message HTML
 * @param {Object} config - Widget configuration
 * @param {string} message - Proactive message
 * @returns {string} Proactive message HTML
 */
export function createProactiveHTML(config, message) {
    return `
        <div class="fyncall-proactive" id="fyncall-proactive">
            <button class="fyncall-proactive-close" aria-label="Close">&times;</button>
            <p>${message || config.proactiveMessage || "👋 Need any help? Feel free to ask!"}</p>
            <button class="fyncall-proactive-btn">Chat with us</button>
        </div>
    `;
}

/**
 * Show proactive message
 * @param {HTMLElement} proactiveElement - Proactive element
 */
export function showProactive(proactiveElement) {
    if (proactiveElement) {
        addClass(proactiveElement, 'show');
    }
}

/**
 * Hide proactive message
 * @param {HTMLElement} proactiveElement - Proactive element
 */
export function hideProactive(proactiveElement) {
    if (proactiveElement) {
        removeClass(proactiveElement, 'show');
    }
}

/**
 * Setup proactive message event handlers
 * @param {HTMLElement} proactiveElement - Proactive element
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onClose - Close callback
 * @param {Function} callbacks.onChat - Chat button callback
 */
export function setupProactiveEvents(proactiveElement, callbacks) {
    if (!proactiveElement) return;

    const closeBtn = proactiveElement.querySelector('.fyncall-proactive-close');
    const chatBtn = proactiveElement.querySelector('.fyncall-proactive-btn');

    if (closeBtn && callbacks.onClose) {
        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            callbacks.onClose();
        });
    }

    if (chatBtn && callbacks.onChat) {
        chatBtn.addEventListener('click', callbacks.onChat);
    }
}
